
import {of as observableOf,  BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import {delay, first, debounceTime, tap, map, filter, takeUntil} from 'rxjs/operators';
import { Component, ContentChild, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective, NgForm } from '@angular/forms';
import { IAnimatedButtonSettings } from '@shared/components/animated-button/animated-button-settings';
import { AnimatedButtonState } from '@shared/components/animated-button/animated-button-state';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ClearError } from '../manager/actions/clear-error';
import { SetButtons } from '../manager/actions/set-buttons';
import { SetError } from '../manager/actions/set-error';
import { SetHasFooter } from '../manager/actions/set-has-footer';
import { SetIcon } from '../manager/actions/set-icon';
import { SetIsDisabled } from '../manager/actions/set-is-disabled';
import { SetStatus } from '../manager/actions/set-status';
import { SetTitle } from '../manager/actions/set-title';
import { IErrorInfo } from '../manager/contracts/error-info';
import { ModalButtons } from '../manager/contracts/modal-buttons';
import { ModalFormStatus } from '../manager/contracts/modal-form-status';
import { ModalHandle } from '../manager/contracts/modal-handle';
import { IState } from '../manager/contracts/state';
import { ModalFormHostManager } from '../manager/modal-form-host-manager';
import { ModalFormHostService } from '../modal-form-host-service/modal-form-host.service';

@Component({
    selector: 'app-components-modal-form-host-ex',
    templateUrl: './modal-form-host.component.html',
    styleUrls: ['./modal-form-host.component.scss']
})
export class ModalFormHostComponent implements OnInit, OnDestroy {
    constructor(
        private service: ModalFormHostService
    ) { }

    private destroyed$ = new ReplaySubject<boolean>();
    private manager = new ModalFormHostManager();

    public modalButtons = ModalButtons;
    public saveButtonState = AnimatedButtonState.Default;
    public modalOptions: Partial<IAnimatedButtonSettings> = {
        enabledOnCompleted: false,
        returnToDefaultState: true
    };

    @Input()
    get handle(): ModalHandle { return this.handle$.value; }
    set handle(value: ModalHandle) { this.handle$.next(value); }
    public handle$: BehaviorSubject<ModalHandle> = new BehaviorSubject<ModalHandle>(undefined);

    @Input()
    set modal(value: BsModalRef) { /* ONLY HERE FOR COMPATIBILITY */ }

    @Input()
    set formId(value: string) { /* ONLY HERE FOR COMPATIBILITY */ }

    @Input()
    set title(value: string) { this.manager.dispatch(new SetTitle(value)); }

    @Input()
    set icon(value: string) { this.manager.dispatch(new SetIcon(value)); }

    @Input()
    set status(value: ModalFormStatus) { /* this.manager.dispatch(new SetStatus(value)); */ }

    @Input()
    set error(value: IErrorInfo) { this.manager.dispatch(new SetError(value)); }

    @Input()
    set buttons(value: number) { this.manager.dispatch(new SetButtons(value)); }

    @Input()
    set hasFooter(value: boolean) { this.manager.dispatch(new SetHasFooter(value)); }

    @Input()
    set isDisabled(value: boolean) { this.manager.dispatch(new SetIsDisabled(value)); }

    get form(): NgForm { return this.form$.value; }
    set form(value: NgForm) { this.form$.next(value); }
    public form$ = new BehaviorSubject<NgForm>(null);

    @ContentChild(FormGroupDirective, { static: true })
    private get formGroup(): FormGroupDirective { return this.formGroup$.value; }
    private set formGroup(value: FormGroupDirective) { this.formGroup$.next(value); }
    private formGroup$ = new BehaviorSubject<FormGroupDirective>(undefined);

    public state: Partial<IState> = { };

    public ngOnInit() {
        this.handle = this.service.current;
        this.manager.state$.pipe(
            takeUntil(this.destroyed$))
            .subscribe(state => this.state = state);

        this.manager.state$.pipe(
            takeUntil(this.destroyed$),
            filter(state => !!state),
            map(state => state.status),
            tap(status => {
                if (status === ModalFormStatus.Iddle) {
                    this.saveButtonState = AnimatedButtonState.Default;
                } else if (status === ModalFormStatus.Pending) {
                    this.saveButtonState = AnimatedButtonState.Submitting;
                } else if (status === ModalFormStatus.Error) {
                    this.saveButtonState = AnimatedButtonState.Error;
                } else if (status === ModalFormStatus.Finished) {
                    this.saveButtonState = AnimatedButtonState.Default;
                }
            }),)
            .subscribe();

        this.service.current$.pipe(
            takeUntil(this.destroyed$),
            filter(handle => !!handle),
            filter(handle => handle.modal === this.handle.modal),
            debounceTime(20),)
            .subscribe(handle => {
                this.form = handle.form;

                if (handle.status === ModalFormStatus.Finished) {
                    observableOf(true).pipe(
                        first(),
                        tap(() => this.saveButtonState = AnimatedButtonState.Success),
                        delay(1000),
                        tap(() => this.handle.modal.hide()),)
                        .subscribe();
                } else if (handle.status === ModalFormStatus.Error) {
                    observableOf(true).pipe(
                        first(),
                        tap(() => this.saveButtonState = AnimatedButtonState.Error),
                        tap(() => this.manager.dispatch(new SetError(handle.error))),)
                        .subscribe();
                }
            });
    }

    public ngOnDestroy(): void {
        this.service.destroyModalRef();
        this.handle = null;

        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    public onAnimatedButtonChanged(state) {
        // do nothing
    }

    public onSubmit(event: Event) {
        this.manager.dispatch(new ClearError(), new SetStatus(ModalFormStatus.Pending));

        let submitting = false;
        if (!!this.form) {
            for (const key in this.form.form.controls) {
                if (this.form.controls.hasOwnProperty(key)) {
                    if (this.form.form.controls[key].invalid) {
                        this.form.form.controls[key].markAsTouched();
                    }
                }
            }

            if (this.form.valid) {
                this.saveButtonState = AnimatedButtonState.Submitting;
                this.form.onSubmit(null);
                submitting = true;
            }
        }

        if (!!this.formGroup) {
            this.formGroup.ngSubmit.emit(this.formGroup.value);
            submitting = true;
        }

        if (!submitting) {
            this.manager.dispatch(new SetStatus(ModalFormStatus.Iddle));
        }
    }

    public onCancel() {
        this.handle.modal.hide();
    }
}
